import type {
  CombinationRow,
  StaffingBandRow,
  StaffingPostRow,
  StaffingProfileRow,
  WorkContentRow,
} from './staffingRows.js';
import type { MetricRow, MetricUpsert } from './metricRows.js';
import type {
  ActivityOverride,
  ActivityQualification,
  BoardActivity,
  BoardDay,
  CleaningRow,
  ServiceGroupRoom,
  ServiceListRow,
} from './boardCoverage.js';
import type { ActivityPresence, BoardAbsence } from './activityBoard.js';
import type { AdminEmployee } from './register.js';
import type {
  AnalysisMessage,
  AnalysisResult,
  Employee,
  EmployeeUpsert,
  TrustedEntry,
  TrustedPerson,
  TrustedPersonRecord,
  DeskListEntry,
  NewStaffMessage,
  IntentActivity,
  ParsedIntent,
  PinCheck,
  PushSubscriptionRecord,
  RosterEntry,
  StaffDigest,
  KnowledgeCandidate,
  StaffMessage,
  TimeEntry,
} from './domain.js';

/**
 * Persistence port for the employee identity backbone. Implemented by
 * `PostgrestEmployeeStore` (Supabase, service role) in production and
 * `FakeEmployeeStore` in tests — the ChatStore pattern (ADR-0007/0009).
 * `ChannelService` depends on this interface, never on Supabase directly.
 */
export interface EmployeeStore {
  /**
   * EMPLOYEES only (`kind = 'employee'`, ADR-0024). The default fails closed: a
   * « Hors Staff » person is never returned here, so no consumer of this port
   * (board, coverage, nudges, name resolution) can count a volunteer as staff.
   * They are reached only through `listTrustedPersons`.
   */
  listEmployees(): Promise<Employee[]>;
  /** Active « Hors Staff » people — the kiosk's second section. Archived ones are excluded. */
  listTrustedPersons(): Promise<TrustedPerson[]>;
  findByPhone(phone: string): Promise<Employee | null>;
  /** Native write-path author resolution (M3) — the auto-link trigger sets this on first sign-in. */
  findByAuthUserId(authUserId: string): Promise<Employee | null>;
  /** Seed path (register import) — merge on employeeId, touching only the register's columns. */
  upsertEmployee(input: EmployeeUpsert): Promise<Employee>;
  /**
   * Kiosk PIN check (S3, Q-M) — the staff_verify_pin RPC in production
   * (service-role-only grant; the hash never leaves Postgres).
   */
  verifyPin(employeeId: string, pin: string): Promise<PinCheck>;
}

/**
 * Persistence port for the bridged message log (append-only, service role —
 * the bridge is the only writer; migration 0015 enforces immutability).
 */
export interface ChannelStore {
  /** Insert unless wa_message_id already exists. Null result = duplicate, dropped. */
  appendMessage(input: NewStaffMessage): Promise<StaffMessage | null>;
  /**
   * Attach an employee to previously unmapped rows from the same sender
   * (permitted by the immutability trigger — mapping backfill only).
   * Returns the number of rows updated.
   */
  backfillEmployee(senderRaw: string, employeeId: string): Promise<number>;
  /** Messages with fromIso <= sent_at < toIso, oldest first (S2 digest input). */
  listMessagesBetween(fromIso: string, toIso: string): Promise<StaffMessage[]>;
}

/**
 * Push-alert port (M2 — docs/adr/0010-staff-mobile-pwa.md §3). Every new
 * channel message fans out (Q-C: every message v1, not just escalations —
 * the staff channel has no single "on-duty" role). Must never throw into the
 * ingest flow; implementations swallow their own delivery errors.
 */
export interface ChannelNotifier {
  /** excludeUserId (M3): skip the author's own subscriptions on an app-authored send. */
  notifyNewMessage(excludeUserId?: string): Promise<void>;
}

/**
 * Persistence port for staff app Web Push subscriptions (M2). Production =
 * `PostgrestPushSubscriptionStore` (service role — reads every subscription
 * for the fan-out); registration/unregistration happens client-side in the
 * staff app under RLS, not through this port.
 */
export interface PushSubscriptionStore {
  list(): Promise<PushSubscriptionRecord[]>;
  /** Drop a subscription the push service reports gone (404/410). */
  remove(endpoint: string): Promise<void>;
}

/**
 * Persistence port for the S2 AI loop (service role — the bridge job is the
 * only writer; operator decisions go through the SQL RPCs, not this port).
 */
export interface AnalysisStore {
  /** Upsert on digest_date — the nightly job may re-run for the same day. */
  saveDigest(digestDate: string, bodyFr: string, messageCount: number): Promise<void>;
  insertCandidates(digestDate: string, bodiesFr: string[]): Promise<void>;
}

/**
 * AI analyzer port (S2). Production = `ClaudeStaffAnalyzer` (one structured
 * Haiku call per day); tests = `FakeStaffAnalyzer`. A thrown error means
 * "AI unavailable" — the job logs and retries the next night; nothing is
 * lost (messages stay in the channel log).
 */
export interface StaffAnalyzer {
  analyze(dateLabelFr: string, messages: AnalysisMessage[]): Promise<AnalysisResult>;
}

/**
 * Persistence port for the time clock (S3, service role — the /api/staff/clock
 * edge function is the only writer; operator corrections go through RLS-gated
 * client writes with the 0018 trace trigger, not this port).
 */
export interface ClockStore {
  /** The employee's open entry (clock_out null), newest first, or null. */
  findOpenEntry(employeeId: string): Promise<TimeEntry | null>;
  /** All open entries — the kiosk's "who is in" indicator. */
  listOpenEntries(): Promise<TimeEntry[]>;
  /** Every entry (any employee) with fromIso <= clock_in < toIso, earliest first — the kiosk's « Aujourd'hui » list. */
  listEntriesBetween(fromIso: string, toIso: string): Promise<TimeEntry[]>;
  openEntry(employeeId: string, clockInIso: string): Promise<TimeEntry>;
  closeEntry(id: number, clockOutIso: string): Promise<TimeEntry>;
}

/**
 * Persistence port for the ACTIVITY-based board (ADR-0011). This one writes the new
 * board_activity_presence / board_absence / board_slot_intent tables. Server
 * (message path) writes go via the service role; the SPAs read + tap-write the
 * same tables directly under RLS.
 */
export interface BoardActivityStore {
  listActivities(): Promise<BoardActivity[]>;
  listQualifications(): Promise<ActivityQualification[]>;
  listOverridesForDay(date: string): Promise<ActivityOverride[]>;
  getDay(date: string): Promise<BoardDay | null>;
  listPresenceForDay(date: string): Promise<import('./activityBoard.js').ActivityPresence[]>;
  listAbsencesForDay(date: string): Promise<import('./activityBoard.js').BoardAbsence[]>;
  listSlotIntentsForDay(date: string): Promise<import('./activityBoard.js').SlotIntent[]>;
  /** Idempotent on (activity, employee, date) — a new write replaces the prior row's status/source/provenance. */
  upsertPresence(input: import('./activityBoard.js').ActivityPresence): Promise<void>;
  deletePresence(activityId: number, employeeId: string, date: string): Promise<void>;
  setAbsence(input: import('./activityBoard.js').BoardAbsence): Promise<void>;
  clearAbsence(employeeId: string, date: string): Promise<void>;
  insertSlotIntent(input: import('./activityBoard.js').SlotIntent): Promise<void>;
  /** Replace step for a message: drop every message-sourced row for (employee, day) across all three tables before writing the new state. */
  clearMessageDay(employeeId: string, date: string): Promise<void>;
}

/**
 * Natural-language intent extraction (M4, v2). Production = `ClaudeIntentExtractor`
 * (one Haiku call per message, closed schema); a thrown error or a 'none'
 * result both mean "just a chat message" — the send path never blocks on this.
 * v2: the model also receives the activity list, and may return several dates
 * and/or named activities.
 */
export interface IntentExtractor {
  extract(
    body: string,
    /** The linked author — the model resolves « je » to this id. */
    authorEmployeeId: string,
    roster: RosterEntry[],
    activities: IntentActivity[],
    todayIso: string,
  ): Promise<ParsedIntent>;
}

/**
 * Read port for the activity board (ladder step 6, closing part of F2).
 *
 * WHY THIS EXISTS. C3's board reads were inlined in its React views — 61 of the
 * 131 boundary violations, spread over eight components, with the PostgREST
 * column lists duplicated file by file (`DAY_COLUMNS` appeared in four). That
 * made the schema part of the UI's contract: no unit test could exercise a
 * board use case, and a column rename meant a component-by-component sweep.
 *
 * The queries collapse to a handful of shapes because they always ask for a
 * DATE RANGE. Single-day reads are `range(d, d)`, which is why there is no
 * separate `day()` method — one shape, not two.
 *
 * Implemented by `SupabaseBoardStore` (the browser's authed client, under RLS)
 * and `FakeBoardStore` in tests. Both are bound in a composition root; a view
 * receives the port and never names a table.
 *
 * Reads only. The write path is ladder step 7.
 */
export interface BoardStore {
  /**
   * Demand figures per day, ascending. `from`/`to` inclusive (ISO dates).
   *
   * At most one row per date (`board_days` is keyed on it), so the window IS
   * the bound — the widest caller asks for a month. That is why there is no
   * open-ended variant any more: `daysAfter(date)` lost its last caller when
   * the feed took over the occupancy chain (M4, 2026-09-01) and was removed on
   * 2026-10-01, since PostgREST cuts a select at 1 000 rows without an error
   * and the table already held 376 days ahead.
   */
  days(from: string, to: string): Promise<BoardDay[]>;

  /**
   * Tonight's occupancy is chained off the previous night (`nextOccupancy`),
   * so the views need the single day BEFORE their window. Separate from
   * `days()` because it is one column, and the caller wants null-not-found
   * rather than an empty list.
   */
  occupancyOn(date: string): Promise<number | null>;
  /**
   * Who is eating on a date — the sheet the salle lays tables from (0147).
   *
   * A LIST, not a count, and the first thing the mirror has been asked for that
   * is. One row per RÉSERVATION: a group is one party at one table block, so a
   * row per room would print it once per room, each line claiming the whole
   * party's covers and each looking individually correct on paper.
   */
  serviceList(date: string): Promise<ServiceListRow[]>;
  /**
   * A group's breakfasts broken out BY ROOM for the morning of `date` (0158).
   *
   * The detail behind the single « Grpe » line above: that line has the right
   * total and tells the étage nothing about which twenty-three doors to knock
   * on. Per-room figures are PLAN-derived — no measured per-room breakfast
   * exists anywhere, `vega_effectif` being réservation-grained (0139) — and
   * each row carries the group's measured total beside them so the sheet can
   * show a disagreement instead of hiding one.
   */
  serviceGroupRooms(date: string): Promise<ServiceGroupRoom[]>;
  /** The étage's sheet for a date: which rooms, in what state, by floor (0148). */
  cleaningList(date: string): Promise<CleaningRow[]>;
  /** The activity catalogue, in display order. */
  activities(): Promise<BoardActivity[]>;
  /** Which employees are qualified for which activities. */
  qualifications(): Promise<ActivityQualification[]>;
  /** Per-day overrides of an activity's window/requirement. */
  overrides(from: string, to: string): Promise<ActivityOverride[]>;
  /** Declared + assigned presence rows. */
  presence(from: string, to: string): Promise<ActivityPresence[]>;
  /** Day-level absence markers. */
  absences(from: string, to: string): Promise<BoardAbsence[]>;
}

/**
 * Read port for the ROSTER — who works here (ladder step 6, closing part of F2).
 *
 * Separate from `BoardStore` because it is a separate aggregate: the board is
 * about days and coverage, the roster is about people. Folding them together
 * would put two invariants behind one port and make the fake a fake of two
 * things.
 *
 * `EMPLOYEE_COLUMNS` was copy-pasted identically into FIVE views across three
 * apps. It now lives in the adapter, which matters more here than elsewhere:
 * `staff_employees` carries COLUMN grants (migration 0016), so the column list
 * is a permission-sensitive constant, not a style choice.
 *
 * Reads only. Employee WRITES stay on the server-side `EmployeeStore` (the
 * register import) and the `staff_admin_*` RPCs.
 */
export interface RosterStore {
  /**
   * Employees with a live contract — the `staff_current_employees` view, which
   * is what every staffing decision is made against. Ordered by first name.
   */
  current(): Promise<Employee[]>;
  /**
   * Every employee including past ones — needed only where historical rows must
   * still resolve to a name (the message log's authors).
   */
  all(): Promise<Employee[]>;
  /**
   * The signed-in user's employee id, via the `staff_employee_id` RPC, or null
   * when the account is not linked to a register row. The staff app branches on
   * this for every "is this me" decision.
   */
  myEmployeeId(): Promise<string | null>;
}

/**
 * Read port for the TIME CLOCK (ladder step 6, closing part of F2).
 *
 * The third C3 aggregate. Its range is an INSTANT range, not a date range —
 * `clock_in` is `timestamptz`, so it is bounded with real ISO instants, and the
 * business day runs to 04:00 (`businessDayFetchRange`). Getting that wrong
 * silently drops the night shift, which is why it is a distinct port rather
 * than another set of methods on `BoardStore` with subtly different bounds.
 *
 * Half-open on purpose: `>= fromIso`, `< toIso`, matching every call site this
 * replaced. The board's date ranges are inclusive at both ends; these are not,
 * and the difference is real rather than an inconsistency to tidy away.
 *
 * Unlike `BoardStore`, this one DOES take an employee filter: a month of the
 * whole team's punches is a genuinely large read, and the staff app's RLS only
 * exposes the caller's own rows anyway.
 *
 * Reads only. The clock-in/out write path is the server-side `ClockStore`.
 */
export interface TimesheetStore {
  entriesBetween(fromIso: string, toIso: string, employeeId?: string): Promise<TimeEntry[]>;
}

/**
 * Read port for the personnel REGISTER (« Registre » — migrations 0023/0024),
 * the fourth C3 read aggregate (ladder step 6).
 *
 * Deliberately NOT part of `RosterStore`, even though both are "employees".
 * `RosterStore` answers *who can be staffed today* — eight columns, readable by
 * every signed-in employee. This answers *what the personnel file says*:
 * contract history plus RUP identity (SSN, address, birth date, sex). It is
 * operator-only and legally sensitive, so it enters through its own port,
 * behind its own `is_admin`-gated RPC. Merging the two would put RUP columns
 * behind an interface that every SPA holds a reference to.
 *
 * Reads only. Every register WRITE is already an `is_admin`-gated
 * `SECURITY DEFINER` RPC (`staff_admin_upsert_person`,
 * `staff_admin_update_contract`, …) — the aggregate boundary the architecture
 * asks for, and untouched here.
 */
export interface RegisterStore {
  /** Every person on file, current and past, with their contract history. */
  listEmployees(): Promise<AdminEmployee[]>;
}

/**
 * CLIENT read port for the staff channel — the message log and the AI digests
 * (ladder step 6, the fifth and last C3 read aggregate).
 *
 * Not to be confused with `ChannelStore` and `AnalysisStore` above, which are
 * the SERVER-side WRITE ports the bridge and the nightly job use under the
 * service role. Same tables, opposite direction, different privilege — so a
 * separate interface rather than a widened one. Naming them apart is the whole
 * point: a browser must never hold a reference to something that can append to
 * an append-only log.
 */
export interface ChannelReadStore {
  /** Messages with fromIso <= sent_at < toIso, oldest first. Capped — a day's feed, not an archive. */
  messagesBetween(fromIso: string, toIso: string, limit?: number): Promise<StaffMessage[]>;
  /** Full-text-ish search over bodies, newest first. */
  searchMessages(term: string, limit?: number): Promise<StaffMessage[]>;
  /** The most recent digests, newest first. */
  recentDigests(limit?: number): Promise<StaffDigest[]>;
  /**
   * Candidates still awaiting an operator decision, newest first
   * (is_admin-gated by RLS). Returns the full domain record — `status` is
   * always 'pending' here, but narrowing the type would make the port's
   * result incompatible with the domain's own KnowledgeCandidate.
   */
  pendingCandidates(): Promise<KnowledgeCandidate[]>;
}

/**
 * WRITE port for the activity board (ladder step 7) — the driven side.
 *
 * Every method maps to ONE database call. That is not an accident of the
 * current schema: `setPresence` and `setAbsence` each maintain the
 * present-xor-absent invariant across two tables, and migration 0055 moved that
 * into a single transaction precisely because five application code paths had
 * been doing it with two sequential unchecked writes. A port method that
 * decomposed into two calls here would put the bug back one layer up.
 *
 * ARCHITECTURE.md §2: the database transaction is the aggregate boundary, and
 * the TypeScript side is a functional core — it does not enforce this
 * invariant, it delegates to the transaction that can.
 */
/**
 * When the board's figures were last true (METRICS-FEED-BRIEF §6.2, RUNBOOK §20.7).
 *
 * Since M4 the demand columns are written by the Vega feed, not typed. That
 * makes a STOPPED feed invisible: the numbers still render, they are simply
 * old, and nobody is waiting to type them. RUNBOOK §20.7 is blunt about it —
 * "A feed nobody can tell has stopped is a manual entry nobody remembers to
 * do, wearing a better disguise."
 *
 * So the day and week views carry « importé à … », and say so loudly past a
 * day. This is C3 reading C4's audit table through a port with an ACL (R8) —
 * read-only, one direction, and it names no Vega concept: a future PMS feed
 * answers the same question.
 */
export interface FeedProvenanceStore {
  /** Null when nothing has ever been imported. */
  latest(): Promise<{
    importedAt: string;
    /** False when the newest attempt was REFUSED — the figures on screen are
     *  the previous good import, and saying so is the whole point. */
    applied: boolean;
    refusalReason: string | null;
  } | null>;
}

export interface BoardWriteStore {
  /** Declare or assign presence, clearing the day's absence. Atomic (0055). */
  setPresence(input: {
    activityId: number;
    employeeId: string;
    date: string;
    status: 'declared' | 'assigned';
    source: ActivityPresence['source'];
    declaredBy: string | null;
  }): Promise<void>;
  /** Remove one (activity, employee, date) row. */
  clearPresence(activityId: number, employeeId: string, date: string): Promise<void>;
  /** Mark the day absent, clearing that day's presence rows. Atomic (0055). */
  setAbsence(input: {
    employeeId: string;
    date: string;
    source: BoardAbsence['source'];
    declaredBy: string | null;
  }): Promise<void>;
  clearAbsence(employeeId: string, date: string): Promise<void>;

  /**
   * Upsert the demand figures for one or more days, keyed on `date`.
   *
   * Takes DOMAIN objects. Before step 7 the views built the snake_case row
   * themselves (`demandParams` returned `half_boards`, `updated_at`, …), which
   * is how PostgREST's column names ended up inside the desk's form logic.
   */
  saveDays(days: BoardDayUpsert[]): Promise<void>;

  /**
   * Update ONLY the occupancy chain columns for a run of days — the « recalculer
   * la chaîne d'occupation » gesture.
   *
   * Separate from `saveDays` because it is a partial update, and the difference
   * is load-bearing: the demand grid owns arrivals/départs/couverts, and this
   * operation must not touch them. The previous inline call relied on
   * PostgREST's partial-upsert semantics to get that, with nothing in the code
   * saying so.
   */
  saveOccupancyChain(rows: { date: string; occupancy: number; stayovers: number }[]): Promise<void>;

  /**
   * Update ONLY the restaurant columns for one day — the Restaurant row's
   * autosave (2026-08-01).
   *
   * Partial for the same load-bearing reason as `saveOccupancyChain`, mirrored:
   * the Hotel row is explicitly saved by its own button, so an autosave firing
   * mid-edit must not persist half-typed départs/arrivées/occupation.
   */
  /**
   * The desk's ONLY remaining board write (METRICS-FEED-BRIEF §6.1, M4).
   *
   * Was `saveRestaurantDay({date, breakfasts, halfBoards, diners, covers})`.
   * Since 0130 the feed owns `breakfasts` and `half_boards`, so accepting them
   * here would let the desk write values the next import discards within the
   * minute — §6.2's "worse than a field that refuses it".
   *
   * `covers` is gone from the signature too: it is `half_boards + diners`, and
   * after M4 those have different writers, so only the database can combine
   * them without reading a stale half. `board_save_desk_day` does it there.
   */
  saveDeskDay(row: { date: string; diners: number; note: string | null }): Promise<void>;

  /** Per-day override of one activity's window/requirement. Keyed on (activity, date). */
  saveOverride(input: {
    activityId: number;
    date: string;
    windowStart: string | null;
    windowEnd: string | null;
    required: boolean;
    note: string | null;
  }): Promise<void>;

  clearOverride(overrideId: number): Promise<void>;
}

/**
 * What the desk types into the demand grid. `BoardDay` minus the fields the
 * database owns (`updatedAt`) — a write shape, not a read shape, and separate
 * for that reason: reusing `BoardDay` here would invite a view to round-trip a
 * server-set timestamp back as if it were input.
 */
export interface BoardDayUpsert {
  date: string;
  departures: number;
  arrivals: number;
  occupancy: number | null;
  stayovers: number;
  breakfasts: number;
  halfBoards: number;
  diners: number;
  covers: number;
  note: string | null;
}

/**
 * Raised when an activity cannot be deleted because presence history references
 * it (FK RESTRICT, migration 0048).
 *
 * A DOMAIN error, translated from Postgres' `23503` by the adapter. The view
 * used to branch on `error.code === '23503'` itself, which made a PostgREST
 * error code part of the UI's vocabulary; the rule it expresses — « an activity
 * that has been worked is never deleted, only deactivated » — is the business's.
 */
export class ActivityInUseError extends Error {
  constructor(readonly label: string) {
    super(`Activity "${label}" has presence history and cannot be deleted.`);
    this.name = 'ActivityInUseError';
  }
}

/** WRITE port for the activity CATALOGUE (operator-only config, migration 0032). */
export interface ActivityCatalogStore {
  createActivity(input: BoardActivityUpsert): Promise<void>;
  updateActivity(id: number, input: BoardActivityUpsert): Promise<void>;
  /** Throws `ActivityInUseError` when presence history references it. */
  deleteActivity(id: number, label: string): Promise<void>;
  addQualification(activityId: number, employeeId: string): Promise<void>;
  removeQualification(activityId: number, employeeId: string): Promise<void>;
  /** « Activité principale » — what this person NORMALLY does, a subset of their qualifications. */
  setMainActivity(activityId: number, employeeId: string, isMain: boolean): Promise<void>;
}

/**
 * READ + WRITE port for the staffing configuration (predictive-staffing brief
 * §4.5, migration 20260729120100). Separate from `ActivityCatalogStore` because
 * it answers a different question: the catalogue says what activities EXIST,
 * this says HOW MANY PEOPLE each needs and when.
 */
export interface StaffingConfigStore {
  loadConfig(): Promise<StaffingConfigSnapshot>;
  /** Returns the profile id — creating its band and posts needs it. */
  saveProfile(id: number | null, input: StaffingProfileUpsert): Promise<number>;
  deleteProfile(id: number): Promise<void>;
  saveWorkContent(profileId: number, input: WorkContentUpsert): Promise<void>;
  /** Returns the band id — creating its posts needs it. */
  saveBand(id: number | null, profileId: number, fromDemand: number): Promise<number>;
  deleteBand(id: number): Promise<void>;
  savePost(id: number | null, bandId: number, input: StaffingPostUpsert): Promise<void>;
  deletePost(id: number): Promise<void>;
  addCombination(activityA: number, activityB: number): Promise<void>;
  removeCombination(activityA: number, activityB: number): Promise<void>;
}

/** Everything the editor and the resolver need, in one round trip. */
export interface StaffingConfigSnapshot {
  profiles: StaffingProfileRow[];
  bands: StaffingBandRow[];
  posts: StaffingPostRow[];
  workContent: WorkContentRow[];
  combinations: CombinationRow[];
}

/**
 * READ + WRITE port for the metric registry (ADR-0015).
 *
 * One port rather than the read/write split used elsewhere in this file: the
 * registry editor is the only writer, it always reads first, and every write is
 * immediately followed by a reload (the DB's cycle trigger can reject a write
 * the client believed valid, so the client's copy is never authoritative).
 */
export interface MetricStore {
  load(): Promise<MetricRow[]>;
  /** Insert or update. `key` is the identity — immutable after creation. */
  save(input: MetricUpsert): Promise<void>;
  /**
   * Delete a formula metric. Refused by trigger when another formula still
   * references it; the error is surfaced verbatim, since it names the
   * dependents the operator has to fix first.
   */
  remove(key: string): Promise<void>;
}

export interface StaffingProfileUpsert {
  label: string;
  archetype: 'binary' | 'workContent' | 'tiered';
  demandFormula: Record<string, number> | null;
  active: boolean;
  sort: number;
  note: string | null;
}

export interface StaffingPostUpsert {
  activityId: number | null;
  label: string;
  windowStart: string;
  windowEnd: string;
  heads: number;
  sort: number;
}

/** Person-minutes + window bounds. Numbers as the operator types them. */
export interface WorkContentUpsert {
  minutesABlanc: number;
  minutesRecouche: number;
  minutesRecoucheFond: number;
  minutesArrivee: number;
  comfortFloorEquivalents: number;
  deepCleanCadence: number;
  earliestStart: string;
  defaultStart: string;
  latestEnd: string;
  heavyArrivals: number;
  checkInEarly: string;
  checkInNormal: string;
  /** Heads/time trade-off (ADR-0015 §4). `targetFinish` null = minimise heads. */
  maxHeads: number;
  targetFinish: string | null;
}

/** The editable fields of an activity — `BoardActivity` minus its identity. */
export interface BoardActivityUpsert {
  label: string;
  windowStart: string;
  windowEnd: string;
  untimed: boolean;
  windowNote: string | null;
  demandDriver: string;
  demandFormula: Record<string, number> | null;
  minDemand: number | null;
  active: boolean;
  sort: number;
  team: string | null;
  /** ADR-0015 — which registry metric gates this activity, and which one it badges. */
  loadMetricKey: string | null;
  badgeMetricKey: string | null;
}

/**
 * WRITE port for time-clock corrections (operator-only — the kiosk's own
 * clock-in/out path is the server-side `ClockStore`, not this).
 */
export interface TimesheetWriteStore {
  updateEntry(id: number, clockInIso: string, clockOutIso: string | null): Promise<void>;
  deleteEntry(id: number): Promise<void>;
  addEntry(employeeId: string, clockInIso: string, clockOutIso: string | null): Promise<void>;
}

/**
 * WRITE port for the personnel register. Every method is an `is_admin`-gated
 * `SECURITY DEFINER` RPC already — the aggregate boundary the architecture asks
 * for, and the reason there is no migration in this increment.
 */
/**
 * Read port for « Hors Staff » people and their punches (ADR-0024). Back-office
 * only. Punches come through RPCs because RLS hides them from direct table reads
 * — that is what keeps them out of every other timesheet view.
 */
export interface TrustedPersonStore {
  /** Active first, then archived. */
  list(): Promise<TrustedPersonRecord[]>;
  /** Half-open on the clock-in instant (`>= from`, `< to`), earliest first. */
  entriesBetween(fromIso: string, toIso: string): Promise<TrustedEntry[]>;
  /** How long their punches are kept, in days (default 120, ceiling 180). */
  retentionDays(): Promise<number>;
}

/** Operator-only writes for « Hors Staff » people. No login, no contract — ever. */
export interface TrustedPersonWriteStore {
  create(firstName: string, lastName: string, nickname: string | null): Promise<string>;
  archive(id: string, archived: boolean): Promise<void>;
  setPin(id: string, pin: string): Promise<void>;
  /** Insert (`entryId` null) or correct one punch. */
  setEntry(entryId: number | null, personId: string, clockInIso: string, clockOutIso: string | null): Promise<void>;
  deleteEntry(entryId: number): Promise<void>;
  setRetentionDays(days: number): Promise<void>;
}

/** The desk list (ADR-0026): who may identify at the desk's « Qui êtes-vous ? ».
 *  Back-office only; the read is `rh.register.read`, the write `rh.register.write`. */
export interface DeskListStore {
  /** Everyone on the register, employees and Hors Staff; archived last. */
  roster(): Promise<DeskListEntry[]>;
  setListed(id: string, listed: boolean): Promise<void>;
}

export interface RegisterWriteStore {
  /** Insert or update a person's identity + RUP fields. Returns the row id. */
  upsertPerson(params: Record<string, unknown>): Promise<string>;
  /**
   * Create a person AND their first contract in ONE transaction (migration
   * 0056, F15). Use this for a brand-new hire; `upsertPerson` alone is for
   * editing someone who already has a contract on file.
   */
  createHire(personParams: Record<string, unknown>, contractParams: Record<string, unknown>): Promise<string>;
  /**
   * Create or replace a contract PERIOD.
   *
   * Note the caller sequence `upsertPerson` → `upsertContract` for a NEW hire is
   * not atomic — the view even names the half-state in its own error message
   * (« Fiche créée, mais le contrat a échoué »). Recorded as F15; closing it
   * needs a migration, so it is not folded into this increment.
   */
  upsertContract(params: Record<string, unknown>): Promise<void>;
  updateContract(params: Record<string, unknown>): Promise<void>;
  /**
   * The explicit "clôturer" act (migration 20260811) — sets `closedAt` +
   * `motifSortie` atomically via `staff_admin_close_contract`. Separate from
   * `updateContract`: pre-close edits (end date, motif while still open) go
   * through that RPC; this one is the one-way close itself. No reopen path —
   * see RUNBOOK §18.17/18.18 register integrity.
   */
  closeContract(params: Record<string, unknown>): Promise<void>;
  deleteContract(contractId: string): Promise<void>;
  setPin(employeeId: string, pin: string): Promise<void>;
  /**
   * Revoke ALL app access for one employee, atomically (migration 20261001100100,
   * ADR-0023): bans the login, ends sessions, removes the staff role, per-user
   * grants and push subscriptions, unlinks the employee, logs it. Disables, never
   * deletes — the register record and every trace of what they did survive.
   */
  revokeAccess(employeeId: string, reason: string | null): Promise<void>;
}

/** A shift an employee asks the operator to add, or to correct. */
export type ShiftRequestKind = 'add' | 'change';
export type ShiftRequestStatus = 'pending' | 'accepted' | 'modified' | 'rejected' | 'cancelled';

export interface ShiftRequest {
  id: number;
  employeeId: string;
  kind: ShiftRequestKind;
  /** `change` only — the shift being corrected. */
  targetEntryId: number | null;
  /** `change` only — that shift's times AT REQUEST TIME. */
  origClockIn: string | null;
  origClockOut: string | null;
  reqClockIn: string;
  reqClockOut: string;
  reason: string | null;
  status: ShiftRequestStatus;
  decidedAt: string | null;
  decisionNote: string | null;
  /** What was actually applied — differs from `req*` when the operator modified it. */
  finalClockIn: string | null;
  finalClockOut: string | null;
  createdAt: string;
}

export interface ShiftRequestInput {
  kind: ShiftRequestKind;
  targetEntryId: number | null;
  clockInIso: string;
  clockOutIso: string;
  reason: string | null;
}

export type ShiftDecision =
  | { kind: 'accept' }
  | { kind: 'modify'; clockInIso: string; clockOutIso: string }
  | { kind: 'reject' };

/**
 * Port for shift requests (migration 20261001110100). One port, two driving
 * services: the employee's identity-reachable path (send / cancel / list own)
 * and the operator's gated one (list all, decide). A request is never a shift
 * until `decide` accepts it — and that is ONE RPC, so the time entry and the
 * decision commit together.
 */
export interface ShiftRequestStore {
  send(input: ShiftRequestInput): Promise<number>;
  cancel(id: number): Promise<void>;
  /** RLS already scopes an employee's session to their own; the filter makes it explicit. */
  listForEmployee(employeeId: string): Promise<ShiftRequest[]>;
  listPending(): Promise<ShiftRequest[]>;
  listDecided(limit: number): Promise<ShiftRequest[]>;
  decide(id: number, decision: ShiftDecision, note: string | null): Promise<void>;
}

/** WRITE port for the operator's knowledge-candidate decisions (S2, C9 review posture). */
export interface ChannelCurationStore {
  /** Accept, optionally with an edited body — creates the `chat_notices` fact. */
  acceptCandidate(candidateId: string, finalBodyFr: string | null): Promise<void>;
  dismissCandidate(candidateId: string): Promise<void>;
}

/** Tear down a realtime subscription. Returned by every `watch*` method. */
export type Unsubscribe = () => void;

/**
 * What changed on the board, in DOMAIN terms rather than table names.
 *
 * Two signals, not four, and not one:
 * - `'presence'` — who is working (`board_activity_presence`, `board_absence`)
 * - `'demand'`   — how much work there is (`board_days`, `board_activity_overrides`)
 *
 * The split is not cosmetic. `DayView` reloads presence WITHOUT resetting the ✎
 * panel, so the desk can edit several cells in a row, but a demand change resets
 * it. Collapsing to one signal would close the panel under the operator's hands
 * whenever anyone anywhere touched a presence row; expanding to four would put
 * table names back in the view.
 */
export type BoardChange = 'presence' | 'demand';

/**
 * Realtime port for the board (ladder step 7, closing F13).
 *
 * A subscription is not a query, which is why this is its own port rather than
 * a method on `BoardStore`: it is long-lived, it pushes, and it must be torn
 * down. Bolting it onto a read port would have made `FakeBoardStore` a fake of
 * two different things.
 *
 * Deliberately COARSE — it says *what kind* of thing changed, never which row.
 * The board tables are small and every consumer re-reads the visible window
 * anyway; handing over a payload would tempt a view into patching its state
 * from a partial row and drifting from what the database actually holds.
 */
export interface BoardRealtime {
  watchBoard(onChange: (what: BoardChange) => void): Unsubscribe;
}

/**
 * Realtime port for the staff channel (ladder step 7, closing F13).
 *
 * Here the payload IS the point — a chat feed appends the new message rather
 * than re-fetching the day — so this one carries the row. It arrives already
 * mapped to `StaffMessage`: the views used to receive the raw PostgREST payload
 * and call `rowToStaffMessage(payload.new as StaffMessageRow)` themselves, which
 * put the wire shape back in the UI right after step 6 had removed it.
 */
export interface ChannelRealtime {
  watchNewMessages(onInsert: (message: StaffMessage) => void): Unsubscribe;
}
